'use client'
import OptimizedImage from '@/components/OptimizedImage'


import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import MasjidDiscussions from './MasjidDiscussions'
import DonateFlow from './DonateFlow'
import TransparencyPanel, { type TransparencyPeriod } from './TransparencyPanel'
import {
  Accessibility,
  AirVent,
  ArrowRight,
  BadgeCheck,
  Bell,
  BookOpen,
  CalendarDays,
  Car,
  Clock3,
  Compass,
  Droplets,
  Fence,
  Flag,
  Sunrise,
  Sun,
  HandCoins,
  HandHeart,
  Heart,
  HeartHandshake,
  Home,
  Landmark,
  Library,
  MapPin,
  MessageCircle,
  Navigation,
  Sparkles,
  Star,
  Users,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import {
  AIAlert,
  AIBadge,
  AIRecommendation,
  AIShimmer,
  ActivityTimeline,
  Button,
  ButtonLink,
  DashboardSection,
  GridSystem,
  MetricPill,
  PremiumCard,
  PremiumMetricCard,
  StatusBadge,
} from '../../../components/ui'
import styles from './page.module.css'
import type { GraphActivityEvent, GraphNode, GraphTrustScore } from '../../../lib/graph-client'
import { civicEventToGraphActivity, formatGraphTime } from '../../../lib/graph-client'
import { useCivicRealtime } from '../../../hooks/useCivicRealtime'
import { useRequireAuth } from '../../../components/auth/AuthGate'
import { masjidPlaceholder } from '../../../lib/masjid-placeholder'
import { campaignPlaceholder } from '../../../lib/entity-placeholder'
import PhotoLightbox from './PhotoLightbox'
import { sourceBadgeLabel } from '../../../lib/provenance'
import { PRAYER_ORDER, toMin, fmt12, type RawPrayerTimes } from '../../../lib/prayer-next'
import { qiblaBearing, compass } from '../../../lib/qibla'
import AdSlot from '../../../components/ui/AdSlot'
import { formatAddress } from '../../../lib/format-address'
import { iconForLabel } from '../../../lib/icon-map'
import { formatIndianNumber } from '../../../lib/format-number'
import MasjidLocationEditor from '../../../components/MasjidLocationEditor'

/* Part 3/icon-sweep: render the icon-map glyph for a chip/stat label (undefined
   when the map has no confident match, so the chip just shows text). */
function chipIcon(label: string) {
  const I = iconForLabel(label)
  return I ? <I size={13} strokeWidth={1.6} /> : undefined
}

/* AI Profile Writer (Phase 3): fills the About line from real record data
   when the committee hasn't written a description. Cached server-side;
   silent fallback to the static line if the API fails. */
function useAIProfile(slug: string, enabled: boolean) {
  const [profile, setProfile] = useState<string | null>(null)
  const [loading, setLoading] = useState(enabled)
  useEffect(() => {
    if (!enabled) { setLoading(false); return }
    let alive = true
    setLoading(true)
    fetch(`/api/ai/masjid-profile?slug=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setProfile(d?.profile || null) })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [slug, enabled])
  return { profile, loading }
}

/* Health Score AI suggestions (Phase 3): specific actions that would raise
   the score, generated on demand from the same record fields the score uses. */
function HealthSuggestions({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<string[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  const toggle = () => {
    setOpen((v) => !v)
    if (items || loading || failed) return
    setLoading(true)
    fetch(`/api/ai/health-suggestions?slug=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.suggestions?.length) setItems(d.suggestions); else setFailed(true) })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }

  return (
    <div className="hs-wrap">
      <button type="button" className="hs-toggle" onClick={toggle} aria-expanded={open}>
        <Sparkles size={13} strokeWidth={1.5} /> How to improve this score <AIBadge />
      </button>
      {open && (
        <div className="hs-panel">
          {loading ? (
            <AIShimmer lines={3} />
          ) : items ? (
            <ul>{items.map((s) => <li key={s}>{s}</li>)}</ul>
          ) : (
            <p>Suggestions are unavailable right now. Claiming this masjid and completing its profile — facilities, photos, waqf status — raises the score.</p>
          )}
        </div>
      )}
      <style jsx>{`
        .hs-wrap{margin-top:10px}
        .hs-toggle{display:inline-flex;align-items:center;gap:7px;border:1px solid var(--f-line-strong);border-radius:99px;background:#fff;color:var(--f-green-deep);font-size:12px;font-weight:600;padding:7px 14px;cursor:pointer;font-family:inherit;transition:all .2s}
        .hs-toggle:hover{background:rgba(14,124,97,.06)}
        .hs-panel{margin-top:10px;background:#fff;border:1px solid var(--f-line);border-radius:13px;padding:13px 16px;box-shadow:var(--f-shadow)}
        .hs-panel ul{margin:0;padding-left:18px;display:grid;gap:7px}
        .hs-panel li{font-size:12.5px;color:var(--f-slate);line-height:1.5}
        .hs-panel p{font-size:12.5px;color:var(--f-slate-dim);line-height:1.55;margin:0}
      `}</style>
    </div>
  )
}

/* Real calculated prayer times for this masjid's city (Aladhan, cached 1h server-side) */
// Committee per-prayer daily times returned by /api/prayer (real, dashboard-set). Present → the table
// shows these (jamaat/iqamah as the time, adhan as a sub-line) labeled "Confirmed by the masjid"; absent
// → the Aladhan calculated times, always labeled estimated.
type CommitteeMap = Partial<Record<'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha', { adhan: string; iqamah: string }>>

function usePrayerTimes(city: string | null | undefined, slug: string | null | undefined, initial: RawPrayerTimes | null = null) {
  /* SSR-fed; the client refetch only keeps long-lived tabs fresh */
  const [raw, setRaw] = useState<(RawPrayerTimes & { committee?: CommitteeMap | null }) | null>(initial)
  /* HYDRATION SAFETY: the "which prayer is next / countdown / done" computation depends on new Date(),
     so it differs between the server render and client hydration. Gate on `mounted`. */
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    let alive = true
    setMounted(true)
    // SSR `initial` is calculated-only (no committee). When we have a masjid slug we still fetch once so
    // the committee per-prayer times (if any) load and replace the estimated first paint. With no slug
    // and an SSR initial, keep it (no masjid context to enrich).
    if (initial && !slug) return () => { alive = false }
    const q = slug ? `&slug=${encodeURIComponent(slug)}` : ''
    fetch(`/api/prayer?city=${encodeURIComponent(city || 'Hyderabad')}${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.Fajr) setRaw(d) })
      .catch(() => {})
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, slug])

  return useMemo(() => {
    if (!raw) return null
    const committee = raw.committee && typeof raw.committee === 'object' ? raw.committee : null
    const hasCommittee = !!committee
    // A prayer is "confirmed" only when the committee set a (plausible, unsuppressed) time for it.
    // getCommitteeDailyTimes already dropped implausible committee times, so an absent committee slot
    // here means "suppressed or never set" → fall back to the CALCULATED time, labelled separately.
    const committeeHas = (name: typeof PRAYER_ORDER[number]) => {
      const c = committee && committee[name.toLowerCase() as keyof CommitteeMap]
      return !!(c && (c.iqamah || c.adhan))
    }
    // Effective display time per prayer: committee IQAMAH (jamaat) when confirmed, else calculated.
    const timeStr = (name: typeof PRAYER_ORDER[number]) => {
      if (committeeHas(name)) { const c = committee![name.toLowerCase() as keyof CommitteeMap]!; return (c.iqamah || c.adhan || '') }
      return raw[name] || ''
    }
    const adhanOf = (name: typeof PRAYER_ORDER[number]): string | null => {
      if (!committeeHas(name)) return null
      const c = committee![name.toLowerCase() as keyof CommitteeMap]!
      const iq = toMin(c.iqamah || ''); const ad = toMin(c.adhan || '')
      return ad != null && (iq == null || ad !== iq) ? fmt12(ad) : null
    }
    const all = PRAYER_ORDER.map((name) => ({ name, min: toMin(timeStr(name)), adhan: adhanOf(name), confirmed: committeeHas(name) }))
    const valid = all.filter((p): p is { name: typeof PRAYER_ORDER[number]; min: number; adhan: string | null; confirmed: boolean } => p.min !== null)
    const allConfirmed = valid.length > 0 && valid.every((p) => p.confirmed)
    const mixed = valid.some((p) => p.confirmed) && valid.some((p) => !p.confirmed)
    if (!hasCommittee && valid.length < 5) return null // calculated path needs the full day (unchanged)
    if (valid.length === 0) return null

    const vIdxOf = (name: string) => valid.findIndex((v) => v.name === name)
    const rowsWith = (statusOf: (vIdx: number) => string) =>
      all.map((p) => ({
        name: p.name,
        time: p.min != null ? fmt12(p.min) : '—',
        adhan: p.adhan,
        confirmed: p.confirmed,
        status: p.min != null ? statusOf(vIdxOf(p.name)) : 'upcoming',
      }))

    const asrMethod = typeof (raw as { asrMethod?: unknown }).asrMethod === 'string' ? String((raw as { asrMethod?: unknown }).asrMethod) : 'Hanafi'
    if (!mounted) {
      return {
        raw, hasCommittee, allConfirmed, mixed, asrMethod,
        next: { name: valid[0].name, time: fmt12(valid[0].min), countdown: '' },
        rows: rowsWith(() => 'upcoming'),
      }
    }
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes()
    const nextIdx = valid.findIndex((p) => p.min > nowMin)
    const next = nextIdx === -1 ? valid[0] : valid[nextIdx]
    const left = nextIdx === -1 ? next.min + 1440 - nowMin : next.min - nowMin
    return {
      raw, hasCommittee, allConfirmed, mixed, asrMethod,
      next: { name: next.name, time: fmt12(next.min), countdown: left >= 60 ? `in ${Math.floor(left / 60)}h ${left % 60}m` : `in ${left} min` },
      rows: rowsWith((vIdx) => nextIdx === -1 ? (vIdx === 0 ? 'next' : 'completed') : vIdx < nextIdx ? 'completed' : vIdx === nextIdx ? 'next' : 'upcoming'),
    }
  }, [raw, mounted])
}


type Tone = 'emerald' | 'gold' | 'danger' | 'warning' | 'info' | 'muted'

type Masjid = {
  id: string
  slug: string
  nameEnglish: string
  nameUrdu?: string | null
  city?: string | null
  district?: string | null
  state?: string | null
  pincode?: string | null
  addressLine1?: string | null
  sect?: string | null
  capacity?: number | null
  establishedYear?: number | null
  verified?: boolean | null
  description?: string | null
  contactPhone?: string | null
  whatsappNumber?: string | null
  contactEmail?: string | null
  imamName?: string | null
  website?: string | null
  facebookUrl?: string | null
  instagramUrl?: string | null
  youtubeUrl?: string | null
  twitterUrl?: string | null
  upiId?: string | null
  upiName?: string | null
  showWhatsappContact?: boolean | null
  whatsappContactNumber?: string | null
  whatsappContactName?: string | null
  prayerTimes?: { jumuah?: string | null; [k: string]: any } | null
  latitude?: string | number | null
  longitude?: string | number | null
  coverPhoto?: string | null
  photo?: string | null
  photos?: string[] | null
  photoReady?: boolean | null
  googlePhotoCount?: number | null
  facilities?: string[] | null
  isWaqfRegistered?: boolean | null
  isWaqf?: boolean | null
  subscriptionStatus?: string | null
  plan?: string | null
  // TIER GATE (server-authoritative): true when verified OR a paid tier (basic/pro).
  // Gates the social-icons row + the YouTube nocookie embed. Donate stays verified-only.
  showSocialEmbeds?: boolean | null
  // CANONICAL claim state (operator-based; NOT derived from `verified`).
  isClaimed?: boolean | null
  claimState?: 'claimed' | 'pending' | 'unclaimed' | null
  // Origin/provenance — drives the honest source badge (OSM / Google / Community).
  importSource?: string | null
}





const MASJID_GRAPH_IDS: Record<string, string> = {
  'sample-masjid': 'masjid:a6f6b8fb',
  'masjid-e-noor': 'masjid:a6f6b8fb',
  'masjid-rahmaniya': 'masjid:db3796f5',
  'masjid-bilal': 'masjid:bc2d30c1',
}

const DEMO_SLUG_ALIASES: Record<string, string> = {
  'sample-masjid': 'masjid-e-noor',
  'jamia-charminar': 'jamia-masjid-charminar',
}

function findDemoMasjid(_slug: string): Masjid | null {
  // H3: never fall back to a demo masjid. A slug not in the DB renders the honest
  // not-found profile (the caller already handles null).
  return null
}

function getPlan(masjid: Masjid) {
  // '' = unclaimed / no tier — the `free` tier no longer exists.
  return masjid.subscriptionStatus || masjid.plan || ''
}

function getPhotos(masjid: Masjid) {
  const photos = masjid.photos?.filter(Boolean) || []
  const cover = masjid.coverPhoto || masjid.photo
  const real = [cover, ...photos].filter(Boolean) as string[]
  // Uploaded media ALWAYS wins (first-party). Rendered via OptimizedImage, unchanged.
  if (real.length) return { list: real, hasReal: true, google: false }
  // No uploaded media → Google Places fallback when photo_ready, else branded placeholder.
  // These are proxied, ToS-compliant URLs; the client renders them via <img onError> so a
  // 404 degrades to the placeholder (never a broken image).
  if (masjid.photoReady && masjid.id) {
    const n = Math.max(1, Math.min(masjid.googlePhotoCount || 1, 10))
    const google = Array.from({ length: n }, (_, i) => `/api/masjids/${masjid.id}/photo?index=${i}&w=1600`)
    return { list: google, hasReal: true, google: true }
  }
  return { list: [masjidPlaceholder(masjid.slug)], hasReal: false, google: false }
}

function locationLine(masjid: Masjid) {
  return formatAddress(masjid.addressLine1, masjid.district, masjid.city, masjid.state) || 'Location being verified'
}

/** Prepend https:// when a link is scheme-less — the same idiom used inline for the website link. */
function normalizeLink(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/**
 * Parse a YouTube URL into an embeddable VIDEO id or a CHANNEL link. Pure (no DOM/network).
 *   video   → youtube.com/watch?v=ID · youtu.be/ID · youtube.com/embed/ID · youtube.com/shorts/ID
 *   channel → any other youtube host (/@handle, /channel/, /c/, /user/, bare channel URL)
 *   null    → empty, unparseable, or a non-youtube host
 */
function parseYoutube(
  raw: string | null | undefined,
): { kind: 'video'; id: string } | { kind: 'channel'; url: string } | null {
  const value = String(raw || '').trim()
  if (!value) return null
  let u: URL
  try {
    u = new URL(normalizeLink(value))
  } catch {
    return null
  }
  const host = u.hostname.replace(/^www\./, '').toLowerCase()
  const isYoutube = host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com' || host === 'youtu.be'
  if (!isYoutube) return null
  const idOk = (id: string) => /^[A-Za-z0-9_-]{6,}$/.test(id)
  const parts = u.pathname.split('/').filter(Boolean)
  // youtu.be/ID — short links are always a video
  if (host === 'youtu.be') {
    return parts[0] && idOk(parts[0]) ? { kind: 'video', id: parts[0] } : { kind: 'channel', url: normalizeLink(value) }
  }
  // youtube.com/watch?v=ID
  const v = u.searchParams.get('v')
  if (v && idOk(v)) return { kind: 'video', id: v }
  // /embed/ID · /shorts/ID
  if ((parts[0] === 'embed' || parts[0] === 'shorts') && parts[1] && idOk(parts[1])) {
    return { kind: 'video', id: parts[1] }
  }
  // Any other youtube URL (/@handle, /channel/, /c/, /user/, bare) → channel link, NOT embedded.
  return { kind: 'channel', url: normalizeLink(value) }
}

// Honest profile completeness — a literal count of real profile fields that are present vs the
// total tracked. No base number, no weighting, no cap: a field counts only if it holds real
// data (non-empty string / non-empty array / true). Purely computed from the live record.
function profileCompleteness(masjid: Masjid) {
  const str = (v: unknown) => typeof v === 'string' && v.trim() !== ''
  const arr = (v: unknown) => Array.isArray(v) && v.some((x) => str(x))
  const prayerTimesSet =
    !!masjid.prayerTimes &&
    typeof masjid.prayerTimes === 'object' &&
    Object.values(masjid.prayerTimes).some((v) => v !== null && v !== undefined && String(v).trim() !== '')
  const checks = [
    masjid.verified === true,                                              // verified status
    prayerTimesSet,                                                        // prayer times set
    arr(masjid.facilities),                                                // ≥1 facility
    str(masjid.coverPhoto) || str(masjid.photo) || arr(masjid.photos),     // ≥1 photo
    str(masjid.contactPhone) || str(masjid.whatsappNumber),                // contact phone OR whatsapp
    str(masjid.imamName),                                                  // imam name
    str(masjid.city) && str(masjid.state) && str(masjid.pincode),          // address complete
    str(masjid.upiId),                                                     // donations enabled (UPI)
    str(masjid.description),                                               // description
  ]
  const total = checks.length
  const done = checks.filter(Boolean).length
  const pct = Math.round((done / total) * 100)
  return { done, total, pct }
}

function money(value: number) {
  if (value >= 100000) return `INR ${(value / 100000).toFixed(1)}L`
  return `INR ${formatIndianNumber(value)}`
}

function percent(raised: number, goal: number) {
  return Math.min(100, Math.round((raised / Math.max(goal, 1)) * 100))
}

function LoadingProfile() {
  return (
    <div className={styles.shell}>
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <p>Loading masjid profile...</p>
      </div>
    </div>
  )
}

function NotFoundProfile() {
  return (
    <div className={styles.shell}>
      <div className={styles.notFound}>
        <Home size={46} />
        <h1>Masjid not found</h1>
        <p>This masjid may not be registered yet, or the profile may still be under review.</p>
        <div>
          <ButtonLink href="/directory" variant="secondary">Back to directory</ButtonLink>
          <ButtonLink href="/onboard">Register this masjid</ButtonLink>
        </div>
      </div>
    </div>
  )
}

/* Prominent Jumu'ah box — sits directly BELOW the action row, near the top. Shows THIS masjid's
   Jumu'ah time(s): committee slots (Confirmed, multi-slot) > the registered single
   (masjids.prayer_times.jumuah) > the calculated Dhuhr (Estimated) > an HONEST-EMPTY prompt. Every
   state is wired to the ONE "Update Salah Timings" CTA. Never fabricates a time. Self-fetches
   /api/prayer (server-cached) for the committee jumaSlots + source. */
function JumuahBox({ masjid }: { masjid: Masjid }) {
  const slug = String((masjid as any).slug || '')
  const [committee, setCommittee] = useState<Array<{ label: string }>>([])
  const [dhuhr, setDhuhr] = useState<string | null>(null)
  useEffect(() => {
    if (!slug) return
    let alive = true
    fetch(`/api/prayer?city=${encodeURIComponent(masjid.city || 'Hyderabad')}&slug=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return
        setDhuhr(d.Dhuhr ? String(d.Dhuhr) : null)
        setCommittee(d.jumaSource === 'masjid' && Array.isArray(d.jumaSlots) ? d.jumaSlots.map((s: any) => ({ label: String(s.label) })) : [])
      })
      .catch(() => {})
    return () => { alive = false }
  }, [slug, masjid.city])

  const regJuma = masjid.prayerTimes?.jumuah || null
  const regMin = regJuma ? toMin(String(regJuma)) : null
  // Priority: committee (confirmed, may be MULTIPLE) > registered (single) > calculated Dhuhr (estimated) > empty.
  const slots: Array<{ label: string; tag: 'Confirmed' | 'Registered' | 'Estimated' }> =
    committee.length ? committee.map((s) => ({ label: s.label, tag: 'Confirmed' as const }))
      : regMin !== null ? [{ label: fmt12(regMin), tag: 'Registered' as const }]
        : dhuhr ? [{ label: dhuhr, tag: 'Estimated' as const }]
          : []
  const toneFor = (t: string) => (t === 'Confirmed' ? 'emerald' : t === 'Registered' ? 'info' : 'warning') as 'emerald' | 'info' | 'warning'

  return (
    <div style={{ border: '1px solid rgba(16,185,129,0.28)', background: 'linear-gradient(150deg, rgba(16,185,129,0.10), rgba(16,185,129,0.02))', borderRadius: 16, padding: '16px 18px', margin: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, display: 'grid', placeItems: 'center', background: 'rgba(16,185,129,0.14)', color: '#0F9D6C', flexShrink: 0 }}><CalendarDays size={20} /></div>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.03em', color: 'var(--f-slate,#64748b)', textTransform: 'uppercase' }}>Jumu&apos;ah · Friday</div>
      </div>
      {slots.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {slots.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--f-navy,#0B132B)' }}>{s.label}</span>
              <StatusBadge tone={toneFor(s.tag)}>{s.tag === 'Confirmed' ? 'Confirmed by committee' : s.tag}</StatusBadge>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13.5, color: 'var(--f-slate,#64748b)', margin: '0 0 12px' }}>No Jumu&apos;ah timing added yet.</p>
      )}
      {slug && (
        <ButtonLink href={`/masjids/${slug}/report`} variant="outline" size="sm" leftIcon={<Flag size={14} />}>
          <strong>Update Salah Timings</strong>
        </ButtonLink>
      )}
    </div>
  )
}

function PrayerLayer({ prayer, masjid, prayerTrust = { kind: 'none', date: null } }: { prayer: ReturnType<typeof usePrayerTimes>; masjid: Masjid; prayerTrust?: { kind: 'confirmed' | 'updated' | 'stale' | 'none'; date: string | null } }) {
  const trustConfirmed = prayerTrust.kind === 'confirmed'
  const fmtTrustDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'long', year: 'numeric' }) : ''
  /* "Today at a glance" — all real: Hijri date + sunrise from Aladhan, zawal
     (solar noon) computed as the midpoint of sunrise→maghrib, Jummah at the
     calculated Dhuhr (committee-confirmed only once claimed), Qibla bearing
     computed from this masjid's coordinates. Each item renders only when its
     source data exists — never a fabricated value. */
  const raw: any = prayer?.raw
  // A claimed masjid's committee-set Jummah overrides the calculated placeholder.
  const [juma, setJuma] = useState<{ time: string; source: string } | null>(null)
  useEffect(() => {
    const slug = (masjid as any).slug
    if (!slug) return
    let alive = true
    fetch(`/api/prayer?city=${encodeURIComponent(masjid.city || 'Hyderabad')}&slug=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.Juma) setJuma({ time: String(d.Juma), source: String(d.jumaSource || 'calculated') }) })
      .catch(() => {})
    return () => { alive = false }
  }, [masjid])

  const glance: Array<{ icon: any; label: string; value: string; note?: string }> = []
  if (raw?.hijri) glance.push({ icon: CalendarDays, label: 'Hijri date', value: String(raw.hijri).replace(/ AH$/, ''), note: 'AH' })
  if (raw?.Sunrise && toMin(raw.Sunrise) !== null) glance.push({ icon: Sunrise, label: 'Sunrise', value: fmt12(toMin(raw.Sunrise)!) })
  const sr = raw?.Sunrise ? toMin(raw.Sunrise) : null
  const mg = raw?.Maghrib ? toMin(raw.Maghrib) : null
  if (sr !== null && mg !== null) glance.push({ icon: Sun, label: 'Zawal', value: fmt12(Math.round((sr + mg) / 2)), note: 'solar noon' })
  // Real (committee-set) Jummah when claimed; otherwise the honest calculated placeholder.
  const jumaRealMin = juma?.source === 'masjid' && toMin(juma.time) !== null ? toMin(juma.time) : null
  // Registration/admin-set Jumu'ah (masjids.prayer_times jsonb) — shown when there's
  // no committee-managed time yet, ahead of the calculated placeholder; hidden if unset.
  const regJuma = masjid.prayerTimes?.jumuah || null
  const regJumaMin = jumaRealMin === null && regJuma && toMin(regJuma) !== null ? toMin(regJuma) : null
  if (jumaRealMin !== null) glance.push({ icon: Clock3, label: 'Jummah', value: fmt12(jumaRealMin), note: 'committee' })
  else if (regJumaMin !== null) glance.push({ icon: Clock3, label: 'Jummah', value: fmt12(regJumaMin), note: 'registered' })
  else if (raw?.Dhuhr && toMin(raw.Dhuhr) !== null) glance.push({ icon: Clock3, label: 'Jummah', value: fmt12(toMin(raw.Dhuhr)!), note: 'calculated' })
  const lat = Number(masjid.latitude), lng = Number(masjid.longitude)
  if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) {
    const b = qiblaBearing(lat, lng)
    glance.push({ icon: Compass, label: 'Qibla', value: `${Math.round(b)}° ${compass(b)}`, note: 'from true north' })
  }

  return (
    <div id="prayer-shortcut">
      <DashboardSection title="Namaz Timings & Prayer Times" eyebrow={prayer?.allConfirmed ? (trustConfirmed ? 'Confirmed by the masjid committee' : 'Set by the masjid committee') : prayer?.mixed ? (trustConfirmed ? 'Committee-confirmed + calculated start times' : 'Committee-set + calculated start times') : 'Calculated for this city · estimated'}>
        <div className={styles.prayerGrid}>
          <PremiumCard className={styles.prayerTable}>
            <div className={styles.cardTopline}>
              <span><Clock3 size={15} /> Today&apos;s timings</span>
              {prayer && <StatusBadge tone="emerald">{prayer.next.name} next</StatusBadge>}
            </div>
            <div className={styles.prayerRows}>
              {(prayer?.rows || PRAYER_ORDER.map((name) => ({ name, time: '—', adhan: null as string | null, confirmed: false, status: 'upcoming' }))).map((row) => (
                <div key={row.name} className={`${styles.prayerRow} ${styles[`prayer_${row.status}`]}`}>
                  <span>{row.name}</span>
                  <strong>{row.time}</strong>
                  {/* Confirmed committee rows show the adhan sub-line; a row with NO committee time falls
                      back to the CALCULATED start time, labelled distinctly (never shown as jamaat). */}
                  {('confirmed' in row) && !row.confirmed && row.time !== '—'
                    ? <small style={{ color: '#b45309', fontWeight: 800 }}>{row.name === 'Asr' ? `Calculated start time (${prayer?.asrMethod || 'Hanafi'})` : 'Calculated start time'}</small>
                    : <small>{row.adhan ? `Adhan ${row.adhan}` : row.status === 'next' ? 'Up next' : row.status === 'completed' ? 'Done' : ''}</small>}
                  <em>{row.status === 'next' && prayer ? prayer.next.countdown : ''}</em>
                </div>
              ))}
            </div>
            <p className={styles.prayerFootnote}>
              {prayer?.allConfirmed
                ? (trustConfirmed ? 'Jamaat times confirmed by the masjid committee.' : 'Jamaat times set by the masjid committee.')
                : prayer?.mixed
                  ? `Rows marked “calculated start time” are estimated for this city; the rest are jamaat times ${trustConfirmed ? 'confirmed' : 'set'} by the masjid committee.`
                  : 'Estimated — calculated for this city. Jamaat schedules are confirmed by the committee once the masjid is claimed.'}
            </p>
            {/* PUBLIC TRUST LINE — only where the committee set jamaat times. confirmed → date;
                updated → committee's last save date; stale → not confirmed in 30d; none → nothing.
                Never shows our SQL-fix date as a committee date (handled server-side). */}
            {(prayer?.allConfirmed || prayer?.mixed) && prayerTrust.kind !== 'none' && (
              prayerTrust.kind === 'confirmed'
                ? <p className={styles.prayerFootnote} style={{ color: '#0E7C61', fontWeight: 700 }}>✓ Timings confirmed by the masjid committee on {fmtTrustDate(prayerTrust.date)}.</p>
                : prayerTrust.kind === 'updated'
                  ? <p className={styles.prayerFootnote} style={{ color: '#0E7C61', fontWeight: 700 }}>Last updated by the masjid committee on {fmtTrustDate(prayerTrust.date)}.</p>
                  : <p className={styles.prayerFootnote} style={{ color: '#b45309', fontWeight: 700 }}>Not confirmed in the last 30 days. Timings may have changed.</p>
            )}
          </PremiumCard>

          <PremiumCard className={styles.glanceCard}>
            <div className={styles.cardTopline}>
              <span><Sun size={15} /> Today at a glance</span>
            </div>
            {glance.length ? (
              <div className={styles.glanceGrid}>
                {glance.map((g) => {
                  const Icon = g.icon
                  return (
                    <div key={g.label} className={styles.glanceItem}>
                      <span className={styles.glanceIcon}><Icon size={15} /></span>
                      <div>
                        <small>{g.label}</small>
                        <strong>{g.value}{g.note ? <em> {g.note}</em> : null}</strong>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className={styles.prayerFootnote}>Sunrise, zawal, Jummah and Qibla appear once this masjid&apos;s location data is available.</p>
            )}
          </PremiumCard>
        </div>
      </DashboardSection>
    </div>
  )
}

function FeedCard({ item }: { item: { type: string; title: string; body: string; time: string; reactions: number; comments: number; tone: Tone } }) {
  return (
    <PremiumCard className={styles.feedCard}>
      <div className={styles.feedHeader}>
        <StatusBadge tone={item.tone}>{item.type}</StatusBadge>
        <span>{item.time}</span>
      </div>
      <h3>{item.title}</h3>
      <p>{item.body}</p>
      <div className={styles.feedTrust}>
        <BadgeCheck size={14} />
        <span>Trusted committee source</span>
        <Sparkles size={14} />
        <span>AI summary</span>
      </div>
      <div className={styles.feedActions}>
        <button type="button"><Heart size={15} /> {item.reactions}</button>
        <button type="button"><MessageCircle size={15} /> {item.comments}</button>
        <button type="button">Share</button>
      </div>
    </PremiumCard>
  )
}

/* PERF P2: server-fed — the profile, prayer times, and stats are in the
   initial HTML; the client only hydrates live elements (realtime, AI, forms).
   The old version client-fetched everything (LCP 10.7s, CLS 1.11 mobile). */
export default function MasjidProfileClient({
  slug,
  initialMasjid,
  initialPrayerTimes,
  nearbyBusinesses = [],
  partOf = [],
  monthlyDonationTotal = 0,
  community = { followers: 0, checkins: 0, visitors: 0 },
  donationEngineOn = false,
  transparencyPeriods = [],
  committee = [],
  prayerTrust = { kind: 'none', date: null },
}: {
  slug: string
  initialMasjid: Masjid | null
  initialPrayerTimes: RawPrayerTimes | null
  nearbyBusinesses?: Array<{ id: string; name: string; category: string; locality: string | null; distanceKm: number | null }>
  partOf?: Array<{ slug: string; group: string; title: string }>
  monthlyDonationTotal?: number
  /** Aggregate-only community counts (see loadCommunityCounts in page.tsx). Never per-user data. */
  community?: { followers: number; checkins: number; visitors: number }
  /** Server-read `donationEngine` flag — gates the public Donate button (Phase 2). Default OFF. */
  donationEngineOn?: boolean
  /** Committee-approved monthly transparency summaries (SSR-fed). Visual held in TransparencyPanel. */
  transparencyPeriods?: TransparencyPeriod[]
  /** PUBLIC leadership (Imam·Khateeb·Chairman·Admin), SSR-fed. Basic renders names+roles with
      initials only — photoUrl is present in the data but intentionally ignored at this tier. */
  committee?: Array<{ role: string; label: string; name: string; photoUrl: string | null }>
  /** Public trust-line state (see getMasjidPrayerTrust): 'confirmed' shows a date + "confirmed" header;
      'updated' shows a committee-save date; 'stale' = not confirmed in 30d; 'none' = no trust line. */
  prayerTrust?: { kind: 'confirmed' | 'updated' | 'stale' | 'none'; date: string | null }
}) {
  const params = { slug }
  const requireAuth = useRequireAuth()
  const [following, setFollowing] = useState(false)
  // Following a masjid is a contributing action — gate it behind a free account,
  // then resume the toggle after sign-in.
  const followMasjid = () => requireAuth({ onAuthed: () => setFollowing((value) => !value), label: 'Sign in to follow this masjid.' })
  // Any logged-in user can suggest an address/location change (always queued for review) — same
  // guest-gate as Follow. Opens the suggest editor (map pin + address) in a modal.
  const [locOpen, setLocOpen] = useState(false)
  const openLocationSuggest = () => requireAuth({ onAuthed: () => setLocOpen(true), label: 'Sign in to suggest an address or location update.' })
  const [submitted, setSubmitted] = useState(false)
  const [masjid, setMasjid] = useState<Masjid | null>(initialMasjid || findDemoMasjid(slug))
  const [loadingMasjid] = useState(false)
  const [notFound] = useState(!initialMasjid && !findDemoMasjid(slug))
  const realtime = useCivicRealtime({ channels: ['global', 'type:prayer', 'type:announcement', 'type:donation', 'type:volunteer'], masjidId: MASJID_GRAPH_IDS[params.slug] || 'masjid:a6f6b8fb', limit: 20 })
  const prayer = usePrayerTimes(masjid?.city, masjid?.slug, initialPrayerTimes)
  const aiProfile = useAIProfile(params.slug, Boolean(masjid && !masjid.description))
  void setMasjid

  const model = useMemo(() => {
    if (!masjid) return null
    const media = getPhotos(masjid)
    const photos = media.list
    const facilities = masjid.facilities || []
    // H5: campaigns were sourced from DEMO_CAMPAIGNS (fabricated). Honest-empty until wired to
    // the real campaigns table for this masjid — the profile hides the section when empty.
    // ⚠ MONEY CORRECTNESS: when you DO wire this, each campaign's `raisedAmount` MUST be the
    // computed value (SUM of real completed donations — src/lib/campaign-raised.ts, or the
    // already-derived GET /api/campaigns), NEVER the stored campaigns.raisedAmount column. This
    // is a CLIENT component, so it cannot call the server-only helper itself — the value must
    // arrive pre-derived from the server/data-source. The reader below gates on raised > 0.
    const campaigns: any[] = []
    return {
      photos,
      hasRealMedia: media.hasReal,
      googleMedia: media.google,
      cover: photos[0],
      facilities,
      campaigns,
      completeness: profileCompleteness(masjid),
    }
  }, [masjid])

  // Google ToS: when the gallery shows Google photos, surface the author attribution
  // (fetched from the photo route's ?meta=1). Fail-soft — no credit shown if unavailable.
  const [googleCredit, setGoogleCredit] = useState<string | null>(null)
  // GALLERY lightbox — index of the clicked thumbnail, or null when closed. View-layer
  // only (the hero never opens this). Uses the SAME model.photos list the gallery renders.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  useEffect(() => {
    if (!model?.googleMedia || !masjid?.id) { setGoogleCredit(null); return }
    let alive = true
    fetch(`/api/masjids/${masjid.id}/photo?meta=1`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !Array.isArray(d?.attributions)) return
        const names = [...new Set((d.attributions as any[]).flat().map((a: any) => a?.displayName).filter(Boolean))]
        setGoogleCredit(names.length ? names.slice(0, 3).join(', ') : null)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [model?.googleMedia, masjid?.id])

  if (loadingMasjid) return <LoadingProfile />
  if (notFound || !masjid || !model) return <NotFoundProfile />

  /* CANONICAL claim state (operator-based, from the server model) — NOT derived from
     `verified`. Drives the "Claim this masjid" CTAs / Unclaimed badge honestly. */
  const claimState: 'claimed' | 'pending' | 'unclaimed' =
    masjid.claimState ?? (masjid.isClaimed ? 'claimed' : 'unclaimed')
  /* SAFETY GUARD (data-driven, NOT flag-gated). `classification`/`attributes` come from the
     full-row record (loadMasjid selects *). Fail-safe to 'masjid' when absent. isDormant is
     driven by the DATA alone — a record classified 'dormant' must NEVER render prayer times,
     a claim CTA, or a donate button, regardless of the masjidClassification feature flag.
     (When the flag is ON, page.tsx already redirected dormant records to the rescue page
     before this client renders; when OFF, no redirect fires and this in-place guard is the
     only thing standing between a dormant record and a misleading live profile.) */
  const rec = masjid as Masjid & { classification?: string; attributes?: Record<string, unknown> | null }
  const classification = rec.classification || 'masjid'
  const isDormant = classification === 'dormant'
  /* isMemorial is effectively always false on web (a shaheed record is redirected to the
     memorial archive in page.tsx before this client renders) — kept for parallelism with
     mobile + defense-in-depth. isLifecycleGuarded gates every donate/claim/sponsor CTA so a
     dormant OR shaheed record never solicits. Prayer panels stay on `isDormant` (their copy
     is dormant-specific; a shaheed record never reaches them). For a normal/undefined masjid
     both are false ⇒ the profile renders byte-for-byte as today. */
  const isMemorial = classification === 'shaheed'
  const isLifecycleGuarded = isDormant || isMemorial
  const currentCondition = rec.attributes && typeof rec.attributes === 'object'
    ? ((rec.attributes as Record<string, unknown>).currentCondition as string | undefined) || null
    : null
  /* Suppress ALL claim UI (badges + the scattered "Claim this masjid" CTAs) on a lifecycle
     record by neutralising the claim-state booleans — there is no committee ownership of a
     dormant/shaheed listing. For masjid/undefined these are the exact original values. */
  const isUnclaimed = !isLifecycleGuarded && claimState === 'unclaimed'
  const isPendingClaim = !isLifecycleGuarded && claimState === 'pending'
  /* Deep-link the claim wizard with THIS masjid pre-selected — masjid/claim/page.tsx reads
     these query params and skips the manual search. URL-encode every value. */
  const claimHref = masjid
    ? `/masjid/claim?masjidSlug=${encodeURIComponent(masjid.slug || slug)}&masjidId=${encodeURIComponent(masjid.id || '')}&name=${encodeURIComponent(masjid.nameEnglish || '')}&city=${encodeURIComponent(masjid.city || '')}&locality=${encodeURIComponent(masjid.district || '')}`
    : '/masjid/claim'

  /* Support-button gate — composes ON TOP of lifecycleGuarded (dormant/shaheed always suppress).
     Volunteer/Sponsor unlock only for a CLAIMED + VERIFIED masjid; Donate additionally requires a
     real UPI destination (no upiId ⇒ no Donate, since it would go nowhere). */
  const isClaimedNow = claimState === 'claimed'
  const supportUnlocked = !isLifecycleGuarded && isClaimedNow && masjid.verified === true
  const upiPayId = (masjid.upiId && String(masjid.upiId).trim()) || ''
  const canDonate = supportUnlocked && !!upiPayId
  const donateHref = upiPayId ? `upi://pay?pa=${encodeURIComponent(upiPayId)}${masjid.upiName ? `&pn=${encodeURIComponent(masjid.upiName)}` : ''}` : ''

  /* Real share (replaces the old no-op stub): native share sheet where available, else copy the
     link to the clipboard with a confirmation. `submitted` doubles as the "Link copied" flag. */
  const shareMasjid = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : `https://masjids-in.vercel.app/masjids/${masjid.slug}`
    const title = `${masjid.nameEnglish} — on Masjids.in`
    const text = `${masjid.nameEnglish}${masjid.city ? `, ${masjid.city}` : ''} — on Masjids.in`
    try {
      if (typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function') {
        await (navigator as any).share({ title, text, url })
        return
      }
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url)
        setSubmitted(true)
      }
    } catch { /* user cancelled or share/clipboard unavailable — no fabricated success */ }
  }

  const directionsHref = masjid.latitude && masjid.longitude
    ? `https://maps.google.com/?q=${masjid.latitude},${masjid.longitude}`
    : `https://maps.google.com/?q=${encodeURIComponent(locationLine(masjid))}`
  /* The civic-graph stream is synthetic demo data — NOT real committee posts.
     The profile feed stays empty (quality EmptyState) until committees post
     real announcements (Phase 1R-d). */
  const liveMasjidActivity: GraphActivityEvent[] = []
  const profileFeed: Array<{ type: string; title: string; body: string; time: string; reactions: number; comments: number; tone: Tone }> = []

  return (
    <div className={styles.shell}>

      <main>
        <section className={styles.hero}>
          <div className={styles.heroMedia}>
            {/* LCP element: priority preloads it; fill uses .heroMedia (position:absolute) as its box.
                Google-sourced covers render as a fail-soft <img> (onError → placeholder) rather than
                next/image, so a stale-index 404 degrades cleanly and we don't re-optimize a proxy. */}
            {model.googleMedia ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={model.cover}
                alt={masjid.nameEnglish}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = masjidPlaceholder(masjid.slug) }}
              />
            ) : (
              <OptimizedImage src={model.cover} alt={masjid.nameEnglish} fill sizes="100vw" priority />
            )}
            <div className={styles.heroOverlay} />
            <div className={styles.heroArt} aria-hidden />
            <div className={styles.heroDots} aria-hidden />
          </div>

          <div className={styles.heroContent}>
            <div className={styles.heroCopy}>
              <div className={styles.kicker}>
                {masjid.verified && <StatusBadge tone="emerald"><BadgeCheck size={12} /> Verified masjid</StatusBadge>}
                {isUnclaimed && <StatusBadge tone="warning">Unclaimed listing</StatusBadge>}
                {isPendingClaim && <StatusBadge tone="info">Claim under review</StatusBadge>}
                <StatusBadge tone="gold">Digital HQ</StatusBadge>
              </div>
              <h1>{masjid.nameEnglish}</h1>
              {masjid.nameUrdu && <p className={styles.urduName}>{masjid.nameUrdu}</p>}
              <p className={styles.location}><MapPin size={16} /> {locationLine(masjid)}</p>
              {/* Imam is rendered ONCE, in the leadership block below (single source of truth via
                  loadPublicLeadership: masjid_members Imam → masjids.imam_name fallback). The old
                  standalone "Imam: {masjid.imamName}" header line was removed to avoid drift. */}
              {masjid.website && (
                <p className={styles.location}>
                  <a href={/^https?:\/\//i.test(masjid.website) ? masjid.website : `https://${masjid.website}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Website ↗</a>
                </p>
              )}
              {/* Social links + YouTube embed — TIER-GATED: shown only when verified OR a paid tier
                  (basic/pro) per masjid.showSocialEmbeds (server-authoritative). Honest-empty within
                  that: only the platforms this masjid actually listed render. */}
              {masjid.showSocialEmbeds && (masjid.facebookUrl || masjid.instagramUrl || masjid.youtubeUrl || masjid.twitterUrl) && (
                <p className={styles.location} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {masjid.facebookUrl ? <a href={normalizeLink(masjid.facebookUrl)} target="_blank" rel="noopener noreferrer" aria-label="Facebook" style={{ color: 'inherit', display: 'inline-flex' }}><Facebook size={18} /></a> : null}
                  {masjid.instagramUrl ? <a href={normalizeLink(masjid.instagramUrl)} target="_blank" rel="noopener noreferrer" aria-label="Instagram" style={{ color: 'inherit', display: 'inline-flex' }}><Instagram size={18} /></a> : null}
                  {masjid.twitterUrl ? <a href={normalizeLink(masjid.twitterUrl)} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" style={{ color: 'inherit', display: 'inline-flex' }}><Twitter size={18} /></a> : null}
                  {masjid.youtubeUrl ? <a href={normalizeLink(masjid.youtubeUrl)} target="_blank" rel="noopener noreferrer" aria-label="YouTube" style={{ color: 'inherit', display: 'inline-flex' }}><Youtube size={18} /></a> : null}
                </p>
              )}
              {/* YouTube: embed a real player ONLY for a specific video; a channel URL is left as the
                  icon link above (no embed). Privacy-enhanced host; lazy; constrained so it stays tasteful. */}
              {(() => {
                if (!masjid.showSocialEmbeds) return null
                const yt = parseYoutube(masjid.youtubeUrl)
                if (yt?.kind !== 'video') return null
                return (
                  <div style={{ marginTop: 14, maxWidth: 480, width: '100%' }}>
                    <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${yt.id}`}
                        title="Masjid YouTube video"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        frameBorder={0}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                      />
                    </div>
                  </div>
                )
              })()}
              {masjid.description ? (
                <p className={styles.summary}>{masjid.description}</p>
              ) : aiProfile.loading ? (
                <div className={styles.summary}><AIShimmer lines={2} /></div>
              ) : aiProfile.profile ? (
                <p className={styles.summary}>{aiProfile.profile} <AIBadge /></p>
              ) : (
                <p className={styles.summary}>A living civic institution for prayer, learning, service, support, governance, and community coordination.</p>
              )}

              <div className={styles.heroActions}>
                <Button onClick={followMasjid} leftIcon={<Star size={16} />}>{following ? 'Following' : 'Follow masjid'}</Button>
                {/* Donate only for a claimed+verified masjid WITH a real UPI id → its own UPI. */}
                {canDonate && <ButtonLink href={donateHref} variant="gold" leftIcon={<Heart size={16} />}>Donate</ButtonLink>}
                {/* Volunteer only for claimed+verified → sign up to volunteer AT this masjid (per-masjid form). */}
                {supportUnlocked && <ButtonLink href={`/masjids/${masjid.slug}/volunteer`} variant="secondary" leftIcon={<Users size={16} />}>Volunteer</ButtonLink>}
                <ButtonLink href={directionsHref} target="_blank" variant="ghost" leftIcon={<Navigation size={16} />}>Directions</ButtonLink>
                <Button variant="ghost" onClick={openLocationSuggest} leftIcon={<MapPin size={16} />}>Update address &amp; location</Button>
                <ButtonLink href="/announcements" variant="ghost" leftIcon={<Bell size={16} />}>Announcements</ButtonLink>
                {/* The correction CTA is consolidated into the prominent Jumu'ah box directly below this
                    row ("Update Salah Timings") — one entry, right at the timing data it feeds. */}
              </div>
              {/* Prominent Jumu'ah box — directly below the action row (actions above stay intact). */}
              <JumuahBox masjid={masjid} />
            </div>

            {isDormant ? (
              /* Phase 4c — dormant side panel replaces the calculated next-prayer widget
                 (no prayer times on a dormant record; never fabricated). */
              <PremiumCard surface="dark" className={styles.livePanel}>
                <div className={styles.panelTopline}>
                  <span><HeartHandshake size={15} /> Adopt a Masjid</span>
                  <StatusBadge tone="warning">Waiting for support</StatusBadge>
                </div>
                <div className={styles.nextPrayer}>
                  <span>Status</span>
                  <strong>Waiting for support</strong>
                  <p>{currentCondition ? `Current condition: ${currentCondition}. ` : ''}No longer active for prayers.</p>
                </div>
                <div className={styles.aiSummary}>
                  <HeartHandshake size={15} />
                  <span>Listed so volunteers, donors and NGOs can find it and help bring it back to life.</span>
                </div>
              </PremiumCard>
            ) : (
            <PremiumCard surface="dark" className={styles.livePanel}>
              <div className={styles.panelTopline}>
                <span><Clock3 size={15} /> Prayer status</span>
                <StatusBadge tone="emerald">Calculated</StatusBadge>
              </div>
              <div className={styles.nextPrayer}>
                <span>Next prayer</span>
                <strong>{prayer ? prayer.next.name : '—'}</strong>
                <p>{prayer ? `${prayer.next.time}${prayer.next.countdown ? ` · ${prayer.next.countdown}` : ''} — ${masjid.city || 'Hyderabad'}` : 'Loading calculated times…'}</p>
              </div>
              <div className={styles.heroStats} title={`Profile completeness — ${model.completeness.done} of ${model.completeness.total} tracked profile fields are filled in.`}>
                <MetricPill label="Profile Completeness" value={`${model.completeness.done} of ${model.completeness.total} complete (${model.completeness.pct}%)`} tone="emerald" icon={chipIcon('Profile Completeness')} />
                {/* hide the chip entirely when there's no real capacity value — never render "— Capacity" */}
                {masjid.capacity ? <MetricPill label="Capacity" value={formatIndianNumber(masjid.capacity)} tone="info" icon={chipIcon('Capacity')} /> : null}
              </div>
              <HealthSuggestions slug={params.slug} />
              <div className={styles.aiSummary}>
                <Sparkles size={15} />
                <span>{!isUnclaimed
                  ? `${masjid.verified ? 'Verified profile' : 'Committee-managed'} · ${model.facilities.length} facilities listed${isPendingClaim ? ' · claim under review' : ' · committee-managed updates'}`
                  : `${masjid.verified ? 'Verified listing' : 'Listed from OpenStreetMap'} · ${model.facilities.length} facilities listed · claim this masjid to manage its profile`}</span>
              </div>
            </PremiumCard>
            )}
          </div>
        </section>

        <section className={styles.content}>
          {/* ONE prominent claim CTA for an UNCLAIMED masjid — replaces the scattered "Claim this
              masjid" buttons (the committee-only layers below are hidden until claimed). `isUnclaimed`
              already excludes dormant/shaheed and claimed/pending, so it shows for exactly one case.
              TODO(welcome-email): the claim-APPROVAL handler in the claim flow (NOT this page) should
              fire the "Masjid OS Basic" welcome email once the queued email module ships. */}
          {isUnclaimed && (
            <section className={styles.claimBanner}>
              <div className={styles.claimBannerBody}>
                <span className={styles.claimBannerKicker}><BadgeCheck size={15} /> Are you the committee?</span>
                <h2>Claim {masjid.nameEnglish} to manage it.</h2>
                <p>Claim this masjid to post announcements, run donation campaigns, add committee details &amp; photos — and get your free Masjid OS Basic dashboard.</p>
              </div>
              <div className={styles.claimBannerActions}>
                <ButtonLink href={claimHref} variant="gold" size="lg" rightIcon={<ArrowRight size={16} />}>Claim this masjid</ButtonLink>
                {/* Prospect can try the committee dashboard before claiming — a public, in-memory
                    client-side demo (no login, nothing saved). */}
                <ButtonLink href="/demo/dashboard" variant="secondary" size="lg">Try the committee dashboard first (demo)</ButtonLink>
              </div>
            </section>
          )}

          {/* Row 1: Prayer Times (1-8) + Facilities & Access (9-12), equal height */}
          <div className={styles.row1}>
            <div className={styles.rowPrayer}>
              {/* Phase 4c — the prayer-times card is suppressed for a dormant record and
                  replaced with a calm revival note. Masjid/shaheed paths are untouched
                  (shaheed already redirects away before this renders). */}
              {isDormant ? (
                <DashboardSection title="Adopt a Masjid — waiting for support" eyebrow="Not active for prayers">
                  <PremiumCard>
                    <p style={{ margin: 0, lineHeight: 1.6 }}>
                      {currentCondition ? `Current condition: ${currentCondition}. ` : ''}This masjid has fallen silent — no longer used for prayers. It is listed so volunteers, donors and NGOs can find it and help bring it back to life.
                    </p>
                  </PremiumCard>
                </DashboardSection>
              ) : (
                <PrayerLayer prayer={prayer} masjid={masjid} prayerTrust={prayerTrust} />
              )}
            </div>
            <div className={styles.rowFacilities}>
              <DashboardSection title="Facilities & Access" eyebrow="From this masjid's listing">
                {(() => {
                  const lowered = model.facilities.map((f) => f.toLowerCase())
                  const has = (keys: string[]) => keys.some((k) => lowered.some((f) => f.includes(k)))
                  /* Icons match icon-map.ts (one source of truth): parking→Car,
                     wheelchair→Accessibility, wudu→Droplets, ac→AirVent,
                     nikah→HeartHandshake. */
                  const TILES: Array<{ label: string; icon: any; keys: string[]; askable?: boolean }> = [
                    { label: "Women's section", icon: Users, keys: ['women'] },
                    { label: 'Wudu area', icon: Droplets, keys: ['wudu'] },
                    { label: 'Parking', icon: Car, keys: ['parking'] },
                    { label: 'Wheelchair', icon: Accessibility, keys: ['wheelchair'], askable: true },
                    { label: 'Quran classes', icon: BookOpen, keys: ['quran', 'madrasa', 'class'] },
                    { label: 'Library', icon: Library, keys: ['library'] },
                    { label: 'AC hall', icon: AirVent, keys: ['ac', 'air'] },
                    { label: 'Nikah services', icon: HeartHandshake, keys: ['nikah'], askable: true },
                    { label: 'Graveyard', icon: Fence, keys: ['graveyard', 'cemetery', 'burial'] },
                    { label: 'Hostel', icon: Home, keys: ['hostel', 'guest', 'lodging', 'accommodation'] },
                  ]
                  return (
                    <div className={styles.facilityTilesWrap}>
                      <div className={styles.facilityTiles}>
                        {TILES.map((t) => {
                          const listed = has(t.keys)
                          const state = listed ? 'listed' : t.askable ? 'ask' : 'not'
                          return (
                            <div key={t.label} className={`${styles.facilityTile} ${styles[`ft_${state}`]}`}>
                              <span className={styles.ftIcon}><t.icon size={20} strokeWidth={1.5} /></span>
                              <span className={styles.ftLabel}>{t.label}</span>
                              <span className={styles.ftChip}>{listed ? 'Listed' : t.askable ? 'Ask' : 'Not listed'}</span>
                            </div>
                          )
                        })}
                      </div>
                      <p className={styles.ftFootnote}>Spot something out of date? Use <strong>Update Salah Timings</strong> in the Jumu&apos;ah box near the top — admins review every report before anything changes.</p>
                    </div>
                  )
                })()}
              </DashboardSection>
            </div>
          </div>

          {/* Row 2: Donation (cols 1-7) + Photo/Media (cols 8-12), equal height */}
          <div className={styles.row2}>
          {/* Donation & Campaign Layer — COMMITTEE-ONLY: shown ONLY once the masjid is claimed
              (fully hidden, not a locked placeholder, for unclaimed/pending/dormant/shaheed).
              Photo/Media stays. When hidden, Photo becomes the row's only child → full width. */}
          {isClaimedNow && (
          <DashboardSection title="Donation & Campaign Layer" eyebrow="Transparent giving with published milestones">
            {model.campaigns.length === 1 ? (
              (() => {
                const campaign = model.campaigns[0]
                const raised = Number(campaign.raisedAmount || 0)
                const goal = Number(campaign.goalAmount || 1)
                return (
                  <PremiumCard className={styles.campaignBanner}>
                    <div className={styles.bannerMedia} aria-hidden>
                      {campaign.photo
                        ? <OptimizedImage src={campaign.photo} alt="" fill sizes="(max-width: 900px) 100vw, 420px" />
                        : <OptimizedImage src={campaignPlaceholder(campaign.id)} alt="" fill sizes="(max-width: 900px) 100vw, 420px" />}
                    </div>
                    <div className={styles.bannerBody}>
                      <StatusBadge tone="emerald">{campaign.category || 'Campaign'}</StatusBadge>
                      <h3>{campaign.titleEnglish}</h3>
                      <p>{campaign.story || 'A campaign run by this masjid’s committee.'}</p>
                      {raised > 0 ? (
                        <>
                          <div className={styles.progressTrack}><span style={{ width: `${percent(raised, goal)}%` }} /></div>
                          <div className={styles.campaignMeta}>
                            <strong>{money(raised)}</strong>
                            <span>of {money(goal)}{campaign.donors ? ` · ${campaign.donors} donors` : ''}</span>
                          </div>
                        </>
                      ) : (
                        <div className={styles.campaignMeta}><span>Target: {money(goal)}</span></div>
                      )}
                      <ButtonLink href={`/campaign/${campaign.id}`} size="sm" rightIcon={<ArrowRight size={14} />}>View campaign</ButtonLink>
                    </div>
                  </PremiumCard>
                )
              })()
            ) : model.campaigns.length ? (
              <GridSystem columns={3} className={styles.campaignGrid}>
                {model.campaigns.map((campaign) => {
                  const raised = Number(campaign.raisedAmount || 0)
                  const goal = Number(campaign.goalAmount || 1)
                  return (
                    <PremiumCard key={campaign.id} className={styles.campaignCard}>
                      <div className={styles.cardTopline}>
                        <StatusBadge tone={campaign.category === 'Education' ? 'info' : campaign.category === 'Waqf' ? 'warning' : 'emerald'}>
                          {campaign.category || 'Campaign'}
                        </StatusBadge>
                      </div>
                      <h3>{campaign.titleEnglish}</h3>
                      <p>{campaign.story || 'A campaign run by this masjid’s committee.'}</p>
                      {raised > 0 ? (
                        <>
                          <div className={styles.progressTrack}><span style={{ width: `${percent(raised, goal)}%` }} /></div>
                          <div className={styles.campaignMeta}>
                            <strong>{money(raised)}</strong>
                            <span>of {money(goal)}{campaign.donors ? ` · ${campaign.donors} donors` : ''}</span>
                          </div>
                        </>
                      ) : (
                        <div className={styles.campaignMeta}><span>Target: {money(goal)}</span></div>
                      )}
                      <ButtonLink href={`/campaign/${campaign.id}`} size="sm" rightIcon={<ArrowRight size={14} />}>View campaign</ButtonLink>
                    </PremiumCard>
                  )
                })}
              </GridSystem>
            ) : (
              <PremiumCard className={styles.campaignCard}>
                <h3>No campaigns yet</h3>
                <p>{masjid.verified
                  ? 'This committee hasn’t launched a campaign. Campaigns appear here with milestones and published receipts.'
                  : isUnclaimed
                    ? 'Campaigns unlock once a committee claims this masjid and completes verification.'
                    : isPendingClaim
                      ? 'A claim for this masjid is under review. Campaigns unlock once it’s approved and verification completes.'
                      : 'Campaigns unlock once this masjid completes verification.'}</p>
                {/* Start-a-campaign is verified-gated; the claim CTA shows ONLY when unclaimed. */}
                {masjid.verified ? (
                  <div style={{ marginTop: 12 }}>
                    <ButtonLink href="/dashboard/campaigns" size="sm">Start a campaign</ButtonLink>
                  </div>
                ) : isUnclaimed ? (
                  <div style={{ marginTop: 12 }}>
                    <ButtonLink href={claimHref} size="sm">Claim this masjid</ButtonLink>
                  </div>
                ) : null}
              </PremiumCard>
            )}
          </DashboardSection>
          )}

          <DashboardSection title="Photo / Media Layer" eyebrow="Events, prayer hall, classes, drives">
            {model.hasRealMedia ? (
              <>
              <div className={styles.galleryRow}>
                {model.photos.slice(0, 10).map((photo, index) => (
                  /* Each thumbnail is a button → opens the fullscreen lightbox at THIS index
                     (keyboard-reachable, pointer cursor). .galleryRow img CSS still sizes the
                     box. Google-sourced photos use a fail-soft <img> (onError → placeholder). */
                  <button
                    key={`${photo}-${index}`}
                    type="button"
                    className={styles.galleryThumb}
                    aria-label={`Open photo ${index + 1} of ${Math.min(model.photos.length, 10)}`}
                    onClick={() => setLightboxIndex(index)}
                  >
                    {model.googleMedia ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={photo} alt={`${masjid.nameEnglish} media ${index + 1}`} width={640} height={480} loading="lazy" decoding="async"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = masjidPlaceholder(masjid.slug) }} />
                    ) : (
                      <OptimizedImage src={photo} alt={`${masjid.nameEnglish} media ${index + 1}`} width={640} height={480} sizes="(max-width: 768px) 45vw, 220px" />
                    )}
                  </button>
                ))}
              </div>
              {model.googleMedia && googleCredit ? (
                <p style={{ fontSize: 11, color: 'var(--f-slate, #6b7280)', marginTop: 8 }}>Photos via Google — {googleCredit}</p>
              ) : null}
              <PhotoLightbox
                photos={model.photos.slice(0, 10)}
                startIndex={lightboxIndex ?? 0}
                open={lightboxIndex !== null}
                onClose={() => setLightboxIndex(null)}
                google={model.googleMedia}
                credit={googleCredit}
                placeholderSrc={masjidPlaceholder(masjid.slug)}
                altBase={masjid.nameEnglish}
              />
              </>
            ) : (
              <div className={styles.mediaStrip}>
                <OptimizedImage src={model.photos[0]} alt="" width={240} height={152} sizes="120px" />
                <div>
                  <strong>No photos uploaded yet</strong>
                  <span>{isUnclaimed
                    ? 'Committees add prayer-hall, event and program photos after claiming.'
                    : isPendingClaim
                      ? 'A claim for this masjid is under review — photos can be added once it’s approved.'
                      : 'Committees add prayer-hall, event and program photos from the dashboard.'}</span>
                </div>
                {/* Claim CTA is centralized in the prominent unclaimed banner above — no inline
                    "claim" button here. "Add photos" shows only for a claimed committee. */}
                {isClaimedNow ? (
                  <ButtonLink href="/dashboard" size="sm">Add photos</ButtonLink>
                ) : null}
              </div>
            )}
          </DashboardSection>
          </div>

          {/* Regular donations — DISPLAY-ONLY UPI. Shown ONLY for a verified masjid that
              has set a UPI ID. Money never touches the platform: the donor pays the
              masjid directly via their UPI app. The QR is generated locally (qrcode.react,
              no network). The monthly figure is SELF-REPORTED by the committee and is
              labelled as such — never "raised through Masjids.in". */}
          {masjid.verified === true && masjid.upiId && !isLifecycleGuarded ? (
            <div className={styles.row3}>
              <DashboardSection title="Support this masjid" eyebrow="Donate directly via UPI">
                <PremiumCard className={styles.campaignCard}>
                  <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <div style={{ background: '#fff', padding: 12, borderRadius: 14, border: '1px solid #e6efec', lineHeight: 0, flexShrink: 0 }}>
                      <QRCodeSVG
                        value={`upi://pay?pa=${encodeURIComponent(masjid.upiId)}${masjid.upiName ? `&pn=${encodeURIComponent(masjid.upiName)}` : ''}`}
                        size={156}
                        level="M"
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <p style={{ margin: '0 0 2px', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7a86', fontWeight: 700 }}>UPI ID</p>
                      <p style={{ margin: '0 0 2px', fontSize: 19, fontWeight: 800, color: '#0b132b', wordBreak: 'break-all' }}>{masjid.upiId}</p>
                      {masjid.upiName && <p style={{ margin: '0 0 10px', fontSize: 13, color: '#475569', fontWeight: 600 }}>{masjid.upiName}</p>}
                      <p style={{ margin: '0 0 10px', fontSize: 13, color: '#0E7C61', fontWeight: 700, lineHeight: 1.5 }}>
                        This donation goes directly to the masjid via UPI. Masjids.in does not collect or handle it — your UPI app will confirm the payment instantly.
                      </p>
                      {/* Donation Session Engine (Phase 2) — guided donate flow with a matching
                          reference + donor self-report. Rendered under the SAME display-donate gate
                          as this block (verified + upiId + !isLifecycleGuarded) AND only when the
                          donationEngine flag is ON. The static QR/UPI-ID above stays as the fallback. */}
                      {donationEngineOn && (
                        <DonateFlow masjidId={masjid.id} masjidName={masjid.nameEnglish} />
                      )}
                      {monthlyDonationTotal > 0 && (
                        <p style={{ margin: '0 0 10px', fontSize: 13.5, color: '#334155', fontWeight: 600 }}>
                          Donations reported by the masjid: <strong style={{ color: '#0b132b' }}>₹{formatIndianNumber(monthlyDonationTotal)}</strong> this month{' '}
                          <span style={{ color: '#6b7a86', fontWeight: 600 }}>(self-reported by the masjid)</span>
                        </p>
                      )}
                      {/* WhatsApp button — shown ONLY on informed opt-in AND a DEDICATED
                          whatsappContactNumber (no contactPhone fallback). The number is only
                          ever embedded in the wa.me link, never rendered as standalone text.
                          The contact identity is whatsappContactName (the old imamName line is
                          gone). */}
                      {masjid.showWhatsappContact === true && masjid.whatsappContactNumber && (() => {
                        const digits = String(masjid.whatsappContactNumber).replace(/\D/g, '').replace(/^0+/, '')
                        const wa = digits.length === 10 ? `91${digits}` : digits
                        const text = encodeURIComponent(`Assalamu alaikum, I made a donation to ${masjid.nameEnglish} via UPI and wanted to confirm it was received.`)
                        const label = masjid.whatsappContactName ? `Message ${masjid.whatsappContactName} on WhatsApp` : 'Message the masjid on WhatsApp'
                        return (
                          <a
                            href={`https://wa.me/${wa}?text=${text}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#25D366', color: '#fff', borderRadius: 10, padding: '9px 15px', fontSize: 13, fontWeight: 700, textDecoration: 'none', marginBottom: 10 }}
                          >
                            <MessageCircle size={15} /> {label}
                          </a>
                        )
                      })()}
                      <p style={{ margin: 0, fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>Donate page powered by Masjids.in</p>
                    </div>
                  </div>
                </PremiumCard>
              </DashboardSection>
            </div>
          ) : null}

          {/* Committee-only layers (Announcements · Discussions · Team & transparency) — shown
              ONLY once the masjid is claimed; fully hidden (no locked placeholders) otherwise. */}
          {isClaimedNow && (
          <div className={styles.row3}>
            {/* Announcements hidden for a dormant/shaheed record — a masjid awaiting adoption
                (or a memorial) has no committee to announce anything, and the empty state's
                claim/follow CTAs are equally wrong there. */}
            {!isLifecycleGuarded && (
            <DashboardSection title="Announcements" eyebrow="Updates from this masjid">
              {profileFeed.length ? (
                <div className={styles.feedScroller}>
                  {profileFeed.map((item) => <FeedCard key={item.title} item={item} />)}
                </div>
              ) : (
                <div className={styles.compactEmpty}>
                  <Bell size={18} />
                  <p>{isUnclaimed
                    ? 'Updates appear once a committee claims this masjid.'
                    : isPendingClaim
                      ? 'A claim for this masjid is under review — updates appear once it’s approved.'
                      : 'No updates posted yet.'}</p>
                  {isUnclaimed
                    ? <ButtonLink href={claimHref} size="sm">Claim this masjid</ButtonLink>
                    : <Button onClick={followMasjid} leftIcon={<Star size={15} />}>{following ? 'Following' : 'Follow masjid'}</Button>}
                </div>
              )}
            </DashboardSection>
            )}

            {/* Discussions hidden for a dormant/shaheed record — its "Claim this masjid" CTA
                must not appear on a record with no committee to claim it. */}
            {!isLifecycleGuarded && (
            <DashboardSection title="Discussions" eyebrow="Community threads for this masjid">
              <MasjidDiscussions masjidId={masjid.id} masjidSlug={masjid.slug} masjidName={masjid.nameEnglish} isClaimed={claimState === 'claimed'} />
            </DashboardSection>
            )}

            {/* Public financial transparency — committee-approved monthly category totals (SSR-fed).
                Contribute reuses DonateFlow under the SAME gate as the donate block above
                (verified + upi_id + donationEngine + not lifecycle-guarded); hidden otherwise. */}
            <TransparencyPanel
              periods={transparencyPeriods}
              masjidId={masjid.id}
              masjidName={masjid.nameEnglish}
              donateEnabled={!isLifecycleGuarded && masjid.verified === true && !!upiPayId && donationEngineOn}
            />

            <DashboardSection title="Team & transparency" eyebrow="Institutional trust layer">
              {/* Collapsed: a claimed masjid expands to committee + transparency metrics; an
                  unclaimed one is a single compact line (no empty full-width block). */}
              {!isClaimedNow ? (
                <PremiumCard className={styles.teamCard}>
                  <div className={styles.compactEmpty}>
                    <Users size={18} />
                    <p>{isLifecycleGuarded
                      ? 'Committee & transparency details aren’t listed for this record.'
                      : 'This masjid isn’t claimed yet — claim it to add committee & transparency details.'}</p>
                    {!isLifecycleGuarded && <ButtonLink href={claimHref} size="sm">Claim this masjid</ButtonLink>}
                  </div>
                </PremiumCard>
              ) : (
              <PremiumCard className={styles.teamCard}>
                <div className={styles.trustGrid}>
                  <MetricPill label="Claim status" value={claimState === 'claimed' ? 'Claimed' : isPendingClaim ? 'Under review' : 'Unclaimed'} tone={claimState === 'claimed' ? 'emerald' : isPendingClaim ? 'info' : 'warning'} icon={chipIcon('Registration')} />
                  <MetricPill label="Waqf linkage" value={masjid.isWaqfRegistered || masjid.isWaqf ? 'Linked' : 'Not listed'} tone={masjid.isWaqfRegistered || masjid.isWaqf ? 'gold' : 'muted'} icon={chipIcon('Waqf linkage')} />
                  <MetricPill label="Source" value={claimState === 'claimed' ? 'Committee' : sourceBadgeLabel(masjid.importSource).label} tone="info" icon={chipIcon('Source')} />
                </div>
                {partOf.length > 0 && (
                  <div className={styles.partOf}>
                    <span className={styles.partOfLabel}>Part of</span>
                    <div className={styles.partOfChips}>
                      {partOf.map((c) => (
                        <Link key={c.slug} href={`/masjids/collections/${c.group}/${c.slug}`} className={styles.partOfChip}>{c.title}</Link>
                      ))}
                    </div>
                  </div>
                )}
                {/* COMMUNITY — aggregate counts only (never who follows / who checked in).
                    Render guard: shown when EITHER count > 0, hidden entirely when both are 0.
                    A live "0 followers · 0 check-ins" on a masjid with no community yet reads
                    dead and adds nothing; the counts appear the moment there is something real
                    to report. Numbers are never padded and carry no "+". */}
                {(community.followers > 0 || community.checkins > 0) && (
                  <div className={styles.partOf}>
                    <span className={styles.partOfLabel}>Community</span>
                    <div className={styles.partOfChips}>
                      {community.followers > 0 && (
                        <span className={styles.partOfChip}>
                          {formatIndianNumber(community.followers)} {community.followers === 1 ? 'follower' : 'followers'}
                        </span>
                      )}
                      {community.checkins > 0 && (
                        <span className={styles.partOfChip}>
                          {formatIndianNumber(community.checkins)} {community.checkins === 1 ? 'check-in' : 'check-ins'}
                          {community.visitors > 0 ? ` · ${formatIndianNumber(community.visitors)} ${community.visitors === 1 ? 'visitor' : 'visitors'}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {(() => {
                  /* PUBLIC leadership (Imam·Khateeb·Chairman·Admin) from the real committee roster
                     (SSR-fed). Basic tier: names + roles with INITIALS avatars only — photos are a
                     Pro/waqf surface and are never rendered here even if a photoUrl exists. */
                  const team = committee.map((m) => ({ name: m.name, role: m.label }))
                  return team.length ? (
                    <div className={styles.teamList}>
                      {team.map((member) => (
                        <div key={member.role} className={styles.teamRow}>
                          <div className={styles.avatar} aria-hidden>{member.name.charAt(0).toUpperCase()}</div>
                          <div>
                            <strong>{member.name}</strong>
                            <span>{member.role}</span>
                          </div>
                          <StatusBadge tone={member.role === 'Imam' ? 'gold' : 'emerald'}>Listed</StatusBadge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.compactEmpty}>
                      <Users size={18} />
                      <p>Committee details aren’t listed yet.</p>
                      {/* Claim CTA suppressed for a dormant/shaheed record; the note stays (honest-empty). */}
                      {!isLifecycleGuarded && <ButtonLink href={claimHref} size="sm">Claim this masjid</ButtonLink>}
                    </div>
                  )
                })()}
              </PremiumCard>
              )}
            </DashboardSection>
          </div>
          )}

          {nearbyBusinesses.length > 0 && (
            <DashboardSection title="Businesses near this masjid" eyebrow="From the open business directory · OSM">
              <div className={styles.nearbyList}>
                {nearbyBusinesses.map((b) => (
                  <Link key={b.id} href={`/business/${b.id}`} className={styles.nearbyRow}>
                    <span className={styles.nearbyIcon}><Landmark size={15} /></span>
                    <span className={styles.nearbyName}>{b.name}</span>
                    <span className={styles.nearbyMeta}>{b.category}{b.locality ? ` · ${b.locality}` : ''}{b.distanceKm !== null ? ` · ${b.distanceKm} km` : ''}</span>
                    <ArrowRight size={14} />
                  </Link>
                ))}
              </div>
            </DashboardSection>
          )}

          {/* The national "From the Ummah" rail was removed from the individual masjid page — a
              national feed on one masjid's page is confusing. It lives on home/community instead.
              (UmmahRail component is unchanged and still used elsewhere.) */}

          {/* "Support the institution" donate/sponsor solicitation — COMMITTEE-ONLY: shown ONLY
              once the masjid is claimed (also never on a dormant/shaheed record, which can't be
              claimed anyway). An unclaimed masjid shows the single prominent claim CTA instead. */}
          {isClaimedNow && (
          <section className={styles.finalCta}>
            <div>
              <span><HandHeart size={15} /> Help strengthen this masjid</span>
              <h2>Support the institution that holds this community together.</h2>
              <p>Donate, volunteer, sponsor, attend, share, or invite your family into a verified civic layer for the masjid.</p>
            </div>
            <div className={styles.finalActions}>
              {/* Donate → the masjid's own UPI, only when claimed+verified+upiId. Volunteer/Sponsor
                  → general pages, only when claimed+verified. Share is always available (drives
                  discovery) and performs a REAL share (native sheet or copy-link). */}
              {canDonate && <ButtonLink href={donateHref} variant="gold" leftIcon={<Heart size={16} />}>Donate</ButtonLink>}
              {supportUnlocked && <ButtonLink href={`/masjids/${masjid.slug}/volunteer`} variant="secondary" leftIcon={<Users size={16} />}>Volunteer</ButtonLink>}
              {supportUnlocked && <ButtonLink href="/business" variant="ghost" leftIcon={<HandCoins size={16} />}>Sponsor</ButtonLink>}
              <Button onClick={shareMasjid} leftIcon={<MessageCircle size={16} />}>{submitted ? 'Link copied' : 'Share'}</Button>
            </div>
          </section>
          )}

          {/* The ad sits BELOW the support CTA (Faheem's rule: never between "Team &
              transparency" and the donation ask — an ad must not split the trust layer
              from the support flow). No ads on a dormant/shaheed record. */}
          {!isLifecycleGuarded && (
            <AdSlot placement="masjid_detail" variant="native" city={masjid.city} locality={masjid.district} />
          )}
        </section>
      </main>

      <nav className={styles.mobileDock} aria-label="Masjid quick actions">
        {/* Donate → the masjid's own UPI (claimed+verified+upiId); Help(volunteer) → claimed+verified.
            Route + Updates are navigation, always available. */}
        {canDonate && <a href={donateHref}><Heart size={18} /><span>Donate</span></a>}
        <a href={directionsHref} target="_blank" rel="noreferrer"><Navigation size={18} /><span>Route</span></a>
        {supportUnlocked && <Link href={`/masjids/${masjid.slug}/volunteer`}><Users size={18} /><span>Help</span></Link>}
        <Link href="/announcements"><Bell size={18} /><span>Updates</span></Link>
      </nav>

      {/* Prayer shortcut suppressed on a dormant record — it must not surface prayer times. */}
      {!isLifecycleGuarded && prayer && <a href="#prayer-shortcut" className={styles.prayerShortcut}><Clock3 size={16} /> {prayer.next.name} {prayer.next.time.replace(/\s(AM|PM)/, '')}</a>}

      {/* Suggest an address/location change — any logged-in user; always queued for moderation. The
          editor is seeded from the current values (already on `masjid`), so no re-fetch is needed. */}
      {locOpen && (
        <div role="dialog" aria-modal="true" onClick={() => setLocOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(11,19,43,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '5vh 16px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <strong style={{ fontSize: 16, color: '#0B132B' }}>Update address &amp; location</strong>
              <button type="button" onClick={() => setLocOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <MasjidLocationEditor
              mode="suggest"
              masjidId={masjid.id}
              slug={masjid.slug}
              initial={{
                latitude: masjid.latitude == null ? null : Number(masjid.latitude),
                longitude: masjid.longitude == null ? null : Number(masjid.longitude),
                addressLine1: masjid.addressLine1 ?? '',
                area: (masjid as { area?: string | null }).area ?? '',
                city: masjid.city ?? '',
                district: masjid.district ?? '',
                state: masjid.state ?? '',
                pincode: masjid.pincode ?? '',
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
